import pWaitFor from 'p-wait-for';

export type SendOutcome = 'continue' | 'abort';

export type PollOutcome = 'pending' | 'success' | 'fail';

export type PollWithPeriodicResendOptions = {
  send: () => Promise<SendOutcome>;
  pollOnce: () => Promise<PollOutcome>;
  pollIntervalMs: number;
  timeoutMs: number;
  /** Minimum time between `send` invocations (including the initial send). */
  resendIntervalMs: number;
};

/**
 * Runs an initial `send`, then polls with `pollOnce` until success, fail, or timeout.
 * While the outcome is still pending, re-invokes `send` whenever `resendIntervalMs`
 * has elapsed since the last send (covers lost device commands).
 */
export async function pollWithPeriodicResend(
  options: PollWithPeriodicResendOptions,
): Promise<'success' | 'fail'> {
  const { send, pollOnce, pollIntervalMs, timeoutMs, resendIntervalMs } = options;

  let lastSendAt = 0;

  const runSend = async (): Promise<'fail' | 'ok'> => {
    const outcome = await send();
    lastSendAt = Date.now();
    return outcome === 'abort' ? 'fail' : 'ok';
  };

  const initial = await runSend();
  if (initial === 'fail') {
    return 'fail';
  }

  return pWaitFor(
    async () => {
      if (Date.now() - lastSendAt >= resendIntervalMs) {
        const resend = await runSend();
        if (resend === 'fail') {
          return pWaitFor.resolveWith('fail' as const);
        }
      }

      const poll = await pollOnce();
      if (poll === 'success') {
        return pWaitFor.resolveWith('success' as const);
      }
      if (poll === 'fail') {
        return pWaitFor.resolveWith('fail' as const);
      }
      return false;
    },
    {
      interval: pollIntervalMs,
      timeout: timeoutMs,
    },
  );
}
